import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import type { MultipleChoiceQuestion as MultipleChoiceType } from '@/types/question-types';
import { renderMathInText } from '@/utils/renderMath';
import styles from './MultipleChoiceQuestion.module.scss';

interface MultipleChoiceQuestionProps {
  question: MultipleChoiceType;
  userAnswer?: string[];
  onAnswer: (answer: string[]) => void;
  showExplanation?: boolean;
  isReviewMode?: boolean;
}

export function MultipleChoiceQuestion({
  question,
  userAnswer,
  onAnswer,
  showExplanation = false,
  isReviewMode = false,
}: MultipleChoiceQuestionProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    userAnswer || []
  );

  // Update selected options when userAnswer prop changes
  useEffect(() => {
    if (userAnswer) {
      setSelectedOptions(userAnswer);
    }
  }, [userAnswer]);

  const handleOptionClick = (optionId: string) => {
    if (isReviewMode) return;

    let newSelected: string[];

    if (question.content.allowMultipleAnswers) {
      // Checkbox behavior: toggle selection
      if (selectedOptions.includes(optionId)) {
        newSelected = selectedOptions.filter(id => id !== optionId);
      } else {
        newSelected = [...selectedOptions, optionId];
      }
    } else {
      // Radio behavior: single selection
      newSelected = [optionId];
    }

    setSelectedOptions(newSelected);
    onAnswer(newSelected);
  };

  const getOptionStatus = (optionId: string, isCorrect: boolean) => {
    if (!showExplanation) return 'default';

    const isSelected = selectedOptions.includes(optionId);

    if (isCorrect) {
      return 'correct';
    }

    if (isSelected && !isCorrect) {
      return 'incorrect';
    }

    return 'default';
  };

  return (
    <div className={styles.multipleChoice}>
      <div className={styles.multipleChoice__question}>
        <h3 className={styles.multipleChoice__questionText}>
          {renderMathInText(question.question_text)}
        </h3>
        {question.content.allowMultipleAnswers && (
          <span className={styles.multipleChoice__hint}>
            (Select all that apply)
          </span>
        )}
      </div>

      <div className={styles.multipleChoice__options}>
        {question.content.options.map((option, index) => {
          const isSelected = selectedOptions.includes(option.id);
          const status = getOptionStatus(option.id, option.isCorrect);

          return (
            <motion.button
              key={option.id}
              className={`${styles.multipleChoice__option} ${styles[`multipleChoice__option--${status}`]
                } ${isSelected ? styles['multipleChoice__option--selected'] : ''}`}
              onClick={() => handleOptionClick(option.id)}
              disabled={isReviewMode}
              whileHover={!isReviewMode ? { scale: 1.02 } : {}}
              whileTap={!isReviewMode ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className={styles.multipleChoice__optionIndicator}>
                {question.content.allowMultipleAnswers ? (
                  <div
                    className={`${styles.multipleChoice__checkbox} ${isSelected
                      ? styles['multipleChoice__checkbox--checked']
                      : ''
                      }`}
                  >
                    {isSelected && <span>✓</span>}
                  </div>
                ) : (
                  <div
                    className={`${styles.multipleChoice__radio} ${isSelected ? styles['multipleChoice__radio--checked'] : ''
                      }`}
                  />
                )}
              </div>

              <div className={styles.multipleChoice__optionText}>
                {renderMathInText(option.text)}
              </div>

              {showExplanation && option.isCorrect && (
                <div className={styles.multipleChoice__correctBadge}>✓</div>
              )}
            </motion.button>
          );
        })}
      </div>

      {showExplanation && (
        <motion.div
          className={styles.multipleChoice__explanation}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.multipleChoice__explanationHeader}>
            <strong>Explanation:</strong>
          </div>
          <p className={styles.multipleChoice__explanationText}>
            {renderMathInText(question.explanation)}
          </p>
        </motion.div>
      )}
    </div>
  );
}
